import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ReportPreviewPanel = ({ reportData, loading, selectedMetrics, dateRange }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="text-center py-12">
          <Icon name="FileText" size={64} className="mx-auto mb-4 opacity-50" color="var(--color-muted-foreground)" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Report Preview</h3>
          <p className="text-sm text-muted-foreground">
            Select metrics and date range to generate preview
          </p>
        </div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden">
      <div className="bg-primary text-primary-foreground p-6">
        <h2 className="text-xl font-bold mb-2">Compliance Report Preview</h2>
        <p className="text-sm opacity-90">
          {dateRange?.startDate} to {dateRange?.endDate}
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Target" size={20} color="var(--color-primary)" />
              <span className="text-xs text-muted-foreground">Overall Score</span>
            </div>
            <p className={`text-2xl font-bold ${getScoreColor(reportData?.summary?.overallScore)}`}>
              {reportData?.summary?.overallScore}%
            </p>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
              <span className="text-xs text-muted-foreground">Total Violations</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {reportData?.summary?.totalViolations}
            </p>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <span className="text-xs text-muted-foreground">Critical</span>
            </div>
            <p className="text-2xl font-bold text-error">
              {reportData?.summary?.criticalViolations}
            </p>
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Building2" size={20} color="var(--color-primary)" />
              <span className="text-xs text-muted-foreground">Departments</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {reportData?.summary?.departmentsAnalyzed}
            </p>
          </div>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={20} />
            Compliance Trend
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={reportData?.charts?.complianceTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="score" stroke="var(--color-primary)" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="BarChart3" size={20} />
            Violations by Type
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={reportData?.charts?.violationsByType}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="type" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="var(--color-primary)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Table" size={20} />
            Department Breakdown
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-foreground">Department</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-foreground">Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-foreground">Violations</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-foreground">Employees</th>
                </tr>
              </thead>
              <tbody>
                {reportData?.tables?.map((row, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{row?.department}</td>
                    <td className={`px-4 py-3 text-sm text-center font-bold ${getScoreColor(row?.complianceScore)}`}>
                      {row?.complianceScore}%
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-foreground">{row?.violations}</td>
                    <td className="px-4 py-3 text-sm text-center text-foreground">{row?.employees}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreviewPanel;